import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCr, timeAgo } from "@/lib/utils-ros";
import {
  AlertTriangle, Globe, Layers, Loader2, Megaphone, Monitor, Radio,
  RefreshCcw, Rss, Sparkles, Target, Timer, TrendingDown, TrendingUp,
  Wallet, Zap,
} from "lucide-react";
import { Link } from "react-router-dom";

const CARD = "rounded-[22px] border border-[#C9DCE6] bg-white shadow-[0_8px_22px_rgba(7,83,154,0.06)]";

function IngestionFeed() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ingest/events", { params: { limit: 20 } })
      .then((r) => setEvents(r.data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  const sourceIcon = { website: Globe, linkedin: Rss, twitter: Rss, ergoniq: Monitor };

  return (
    <section className={CARD} data-testid="ingestion-feed">
      <SectionHeader icon={Globe} title="Inbound activity" subtitle="Website · LinkedIn · X · Ergoniq" />
      {loading ? (
        <div className="p-8 text-[13px] text-[#78909C] flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading activity…
        </div>
      ) : events.length === 0 ? (
        <div className="p-8 text-center text-[13px] text-[#78909C]">
          <span className="w-11 h-11 rounded-xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center mx-auto mb-3">
            <Globe size={19} />
          </span>
          No inbound activity has been captured yet.
        </div>
      ) : (
        <div className="divide-y divide-[#E1EDF2] max-h-72 overflow-y-auto">
          {events.map((event) => {
            const Icon = sourceIcon[event.source] || Globe;
            const destination = event.entity_type === "lead" ? "/leads" : event.entity_type === "ticket" ? "/tickets" : "/signals";
            return (
              <div key={event.id} className="px-5 py-3 flex items-start gap-3 text-[12px]" data-testid={`ingest-row-${event.id}`}>
                <span className="w-8 h-8 rounded-xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center shrink-0"><Icon size={13} /></span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="uppercase text-[9px] tracking-widest text-[#07539A] font-semibold">{event.source}</span>
                    <span className="text-[#9AAEB8]">→</span>
                    <span className="font-medium capitalize text-[#243B53]">{event.entity_type}</span>
                    {event.status === "duplicate" && <span className="text-[9px] text-[#C76500]">duplicate</span>}
                  </div>
                  <div className="text-[#607D8B] truncate mt-0.5">
                    {event.company?.name || event.payload_summary?.company_name || event.payload_summary?.subject || event.event_type}
                    {event.payload_summary?.contact_name ? ` · ${event.payload_summary.contact_name}` : ""}
                    {event.payload_summary?.reporter_name ? ` · ${event.payload_summary.reporter_name}` : ""}
                  </div>
                  <div className="text-[10px] text-[#9AAEB8] mt-0.5">{timeAgo(event.created_at)}</div>
                </div>
                {event.entity_id && <Link to={destination} className="text-[11px] text-[#07539A] font-semibold hover:underline shrink-0">View →</Link>}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function Marketing() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  async function load({ refresh = false } = {}) {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const response = await api.get("/marketing/insights");
      setData(response.data);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Marketing data could not be loaded.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <div className="min-h-full bg-[#F5FAFC] p-10 text-[#78909C] flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading marketing performance…</div>;
  }

  if (!data) {
    return (
      <div className="min-h-full bg-[#F5FAFC] p-8">
        <div className={`${CARD} max-w-xl mx-auto p-8 text-center`}>
          <AlertTriangle size={20} className="text-[#C76500] mx-auto mb-3" />
          <div className="font-semibold text-[#243B53]">Marketing data unavailable</div>
          <p className="text-[12px] text-[#78909C] mt-1">{error}</p>
          <button onClick={() => load()} className="h-9 px-4 mt-4 rounded-xl bg-[#07539A] !text-white text-[12px] font-semibold">Try again</button>
        </div>
      </div>
    );
  }

  const { stats = {}, ai = {}, kpis = {}, ai_available: aiAvailable = false } = data;
  const channels = Object.entries(stats.channel_performance || {}).sort(([, a], [, b]) => b.count - a.count);
  const industries = Object.entries(stats.industry_performance || {}).sort(([, a], [, b]) => b.pipeline_cr - a.pipeline_cr);

  return (
    <div className="min-h-full bg-[#F5FAFC] pb-12" data-testid="marketing-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-7 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight !text-white">Marketing Insights</h1>
            <p className="text-[13px] text-white/75 mt-1">Pipeline performance, demand sources, and campaign direction in one view.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-left md:text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/60">Niq analysis</div>
              <div className={`text-[12px] font-semibold ${aiAvailable ? "text-[#A8E4C8]" : "text-[#B9D8EA]"}`}>{aiAvailable ? "Available" : "Unavailable"}</div>
            </div>
            <button onClick={() => load({ refresh: true })} disabled={refreshing} data-testid="mkt-refresh" className="h-10 px-4 rounded-xl border border-white/35 bg-white/10 hover:bg-white/15 !text-white text-[12px] font-semibold flex items-center gap-2 disabled:opacity-60">
              {refreshing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCcw size={13} />} Refresh
            </button>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="mkt-revenue-kpis">
          <MetricCard icon={Layers} label="Total pipeline" value={formatCr(kpis.total_funnel_size_cr)} detail={`Weighted ${formatCr(kpis.weighted_funnel_cr)}`} testId="kpi-funnel-size" />
          <MetricCard icon={Timer} label="Lead to won velocity" value={kpis.avg_velocity_days_lead_to_won != null ? `${kpis.avg_velocity_days_lead_to_won} days` : "—"} detail={kpis.velocity_sample_size ? `${kpis.velocity_sample_size} closed deal sample` : "No closed-deal history yet"} testId="kpi-velocity" />
          <MetricCard icon={Wallet} label="Won profitability" value={formatCr(kpis.won_profit_cr)} detail={`Gross margin ${kpis.gross_margin_pct != null ? `${kpis.gross_margin_pct}%` : "—"}`} testId="kpi-profit" />
        </div>

        <div className={`${CARD} grid grid-cols-2 lg:grid-cols-4 overflow-hidden`}>
          <SmallMetric label="Signals" value={stats.total_signals ?? 0} icon={Radio} />
          <SmallMetric label="Leads" value={stats.total_leads ?? 0} icon={Zap} />
          <SmallMetric label="Opportunities" value={stats.total_opportunities ?? 0} icon={Target} />
          <SmallMetric label="Active pipeline" value={formatCr(stats.total_pipeline_cr)} icon={TrendingUp} last />
        </div>

        <section className={CARD} data-testid="marketing-ai-workspace">
          <SectionHeader icon={Sparkles} title="Niq's marketing analysis" subtitle={aiAvailable ? "Generated from the current CRM pipeline" : "AI credentials are not connected"} badge={aiAvailable ? "Ready" : "Unavailable"} />
          {aiAvailable ? (
            <div className="p-5 space-y-4">
              <div className="rounded-2xl bg-[#EAF5FA] border border-[#D2E5ED] p-4">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-[#07539A] mb-2">Executive summary</div>
                <p className="text-[13px] leading-relaxed text-[#435F70]">{ai.executive_summary || "No summary was returned."}</p>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <InsightList icon={TrendingUp} title="What's working" items={ai.what_is_working} />
                <InsightList icon={TrendingDown} title="Needs attention" items={ai.what_is_not_working} />
                <InsightList icon={Megaphone} title="Campaign ideas" items={ai.campaign_recommendations} />
              </div>
            </div>
          ) : (
            <div className="p-5 grid md:grid-cols-[1.25fr_1fr_1fr] gap-3">
              <UnavailableBlock title="Executive summary" large />
              <UnavailableBlock title="What's working" />
              <UnavailableBlock title="Campaign recommendations" />
            </div>
          )}
        </section>

        <div className="grid lg:grid-cols-2 gap-5">
          <PerformanceCard title="Signal sources" icon={Radio} emptyText="No signal-source data yet." hasItems={channels.length > 0}>
            {channels.map(([name, channel]) => (
              <div key={name} className="grid grid-cols-[1fr_auto] gap-4 px-5 py-3 border-t border-[#E1EDF2] items-center">
                <div><div className="text-[12px] font-semibold text-[#243B53] capitalize">{name.replaceAll("_", " ")}</div><div className="text-[10px] text-[#78909C] mt-0.5">Average strength {channel.avg_strength}</div></div>
                <div className="text-[13px] font-semibold tabular-nums text-[#07539A]">{channel.count}</div>
              </div>
            ))}
          </PerformanceCard>

          <PerformanceCard title="Industry pipeline" icon={TrendingUp} emptyText="No industry pipeline data yet." hasItems={industries.length > 0}>
            {industries.map(([name, industry]) => (
              <div key={name} className="grid grid-cols-[1fr_auto] gap-4 px-5 py-3 border-t border-[#E1EDF2] items-center">
                <div><div className="text-[12px] font-semibold text-[#243B53] capitalize">{name}</div><div className="text-[10px] text-[#78909C] mt-0.5">{industry.count} deal{industry.count === 1 ? "" : "s"} · {industry.won} won · {industry.lost} lost</div></div>
                <div className="text-[13px] font-semibold tabular-nums text-[#07539A]">{formatCr(industry.pipeline_cr)}</div>
              </div>
            ))}
          </PerformanceCard>
        </div>

        <IngestionFeed />
      </div>
    </div>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, badge }) {
  return (
    <div className="px-5 py-4 border-b border-[#D8E8EF] bg-[#EAF5FA] rounded-t-[21px] flex items-center gap-3">
      <span className="w-9 h-9 rounded-xl bg-white text-[#07539A] flex items-center justify-center border border-[#D4E5EC] shrink-0"><Icon size={15} /></span>
      <div className="min-w-0"><div className="text-[13px] font-semibold text-[#07539A]">{title}</div>{subtitle && <div className="text-[10px] text-[#78909C] mt-0.5 truncate">{subtitle}</div>}</div>
      {badge && <span className="ml-auto px-2.5 py-1 rounded-full bg-white border border-[#C9DCE6] text-[9px] uppercase tracking-widest font-semibold text-[#607D8B]">{badge}</span>}
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, detail, testId }) {
  return (
    <div className={`${CARD} p-5`}><div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-[#607D8B]"><Icon size={13} className="text-[#07539A]" /> {label}</div><div className="font-display text-2xl md:text-3xl tabular-nums text-[#07539A] mt-2" data-testid={testId}>{value}</div><div className="text-[11px] text-[#78909C] mt-1">{detail}</div></div>
  );
}

function SmallMetric({ label, value, icon: Icon, last }) {
  return (
    <div className={`p-4 md:p-5 ${last ? "" : "border-r border-[#E1EDF2]"} border-b lg:border-b-0`}><div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-[#78909C]"><Icon size={12} className="text-[#1687EA]" /> {label}</div><div className="font-display text-2xl tabular-nums text-[#243B53] mt-2">{value}</div></div>
  );
}

function UnavailableBlock({ title, large = false }) {
  return (
    <div className={`rounded-2xl border border-dashed border-[#B8D3DF] bg-[#F7FBFD] p-5 ${large ? "md:min-h-[138px]" : ""}`}><div className="text-[10px] uppercase tracking-widest font-semibold text-[#07539A]">{title}</div><div className="flex items-center gap-2 text-[12px] text-[#78909C] mt-4"><Sparkles size={13} className="text-[#9BB7C4]" /> Niq analysis unavailable</div></div>
  );
}

function InsightList({ icon: Icon, title, items = [] }) {
  return (
    <div className="rounded-2xl border border-[#D6E6ED] bg-white p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-[#07539A]"><Icon size={12} /> {title}</div>
      {items?.length ? <ul className="mt-3 space-y-2">{items.slice(0, 4).map((item, index) => <li key={index} className="text-[11px] leading-relaxed text-[#607D8B]">• {typeof item === "string" ? item : item?.title || item?.text || item?.item || item?.rationale || "Insight"}</li>)}</ul> : <div className="text-[11px] text-[#9AAEB8] mt-3">No insight returned.</div>}
    </div>
  );
}

function PerformanceCard({ title, icon: Icon, emptyText, hasItems, children }) {
  return <section className={CARD}><SectionHeader icon={Icon} title={title} />{hasItems ? children : <div className="p-8 text-center text-[12px] text-[#78909C]">{emptyText}</div>}</section>;
}
